import { PaymentMethod } from './../../interfaces/PaymentMethod';
import {
  DebtResponse,
  PromotionTypeEnum,
  StandardPaymentType,
  TransactionReasonEnum,
} from '../../interfaces/Debt';
import {
  GTMSession,
  GTMCustomer,
  GTMDebt,
  GTMDevice,
  GTMPayment,
  GAnalyticsEventType,
  GTMFlowSteps,
  GTMStepNumber,
  GTMCallBackEvent,
  GTMCallBackPhone,
  GTMCallBackWhen,
  GTMTimeElapse,
  GTMTimeData,
  GTMContinuePaymentMethod,
  GTMPaymentInfo,
  GTMPaymentData,
  GTMContinuePayment,
  GTMPurchaseData,
  GTMChangePaymentOption,
  GTMPaymentPlanData,
  GTMFastPayData,
  GTMUserCargado,
} from '../../interfaces/GTM';
import { PaymentCard, PaymentMethods } from '../../interfaces/Loan';
import {
  PaymentDataLayer,
  ReusePaymentOptionalParams,
  ReusePaymentResponse,
} from '../../interfaces/Payment';
import { UserResponse } from '../../interfaces/UserData';
import {
  checkPhones,
  getBirthYear,
  getCardData,
  getCustAgeSegment,
  getEmailDomain,
  getLoanCount,
  getMaxEndDay,
  getMinStartDay,
  getPaymentPlanCount,
  getPaymentPlanStatesCount,
  getTimeResult,
  recoverPaymentMethod,
} from '../../utils/dataLayerHelper';
import { dataLayerParamAdd, gevent } from '../../utils/gtmConfig';
import { calculateTotalDebtAmount } from '../../utils/paymentsHelper';
import { PaymentPlanStatus } from '../../interfaces/PaymentPlan';
import { setClarityCustomTags } from '../Clarity';
import { clarity } from 'react-microsoft-clarity';

const sessionObj: GTMSession = {
  utmSource: '',
  utmMedium: '',
  utmCampaign: '',
  utmContent: '',
  utmTerm: '',
  viewMode: '',
};

const customerObj: GTMCustomer = {
  cust_age_segment: '',
  cust_birth_year: '',
  cust_gender: '',
  cust_birth_country: '',
  cust_nationality: '',
  cust_marital_status: '',
  cust_has_email: '',
  cust_email_domain: '',
  cust_has_mobile_phone: '',
  cust_card_registered_count: '',
  cust_contract_type: '',
  cust_monthly_income: '',
  cust_status: '',
};

const debtObj: GTMDebt = {
  unpaid_loans_count: '',
  unpaid_installments_count: '',
  unpaid_amount: '',
  unpaid_installment_max_age: '',
  unpaid_has_promotions: '',
  unpaid_has_24h_promotion: '',
  unpaid_max_order_number: '',
  unpaid_personal_loan_count: '',
  unpaid_consumer_loan_count: '',
  allow_payment_plan_count: '',
  payment_plan_installments_paid_count: '',
  payment_plan_installments_unpaid_count: '',
  payment_plan_min_start_day: '',
  payment_plan_max_end_day: '',
};

const deviceObj: GTMDevice = {
  device_google_pay_capable: '',
  device_apple_pay_capable: '',
};

const paymentObj: GTMPayment = {
  payment_id: '',
  payment_transaction_reason: '',
  payment_type: '',
  payment_final_amount: '',
  payment_savings_amount: '',
  payment_promotion_used: '',
  payment_third_used: '',
  payment_result: '',
  payment_card_brand: '',
  payment_iin_number: '',
  payment_method: '',
};

const GAnalyticsService = {
  // **********************************************************************
  // TAGS
  // **********************************************************************

  //session params. Solo se usa para el view mode de momento
  saveSessionDataLayer: (params: URLSearchParams) => {
    sessionObj.viewMode = params.get('viewMode') ?? '';
    sessionObj.utmCampaign = params.get('utm_campaign') ?? '';
    sessionObj.utmSource = params.get('utm_source') ?? '';
    sessionObj.utmMedium = params.get('utm_medium') ?? '';
    sessionObj.utmContent = params.get('utm_content') ?? '';
    sessionObj.utmTerm = params.get('utm_term') ?? '';

    if (sessionObj.viewMode && clarity.hasStarted()) {
      setClarityCustomTags({ view_mode: sessionObj.viewMode });
    }

    dataLayerParamAdd('Session', sessionObj);
  },

  //me/customers
  saveCustomerDataLayer: (
    customer: UserResponse,
    CardsList?: PaymentCard[]
  ) => {
    for (let value of Object.keys(customer)) {
      switch (value) {
        case 'birth_date':
          customerObj.cust_age_segment = getCustAgeSegment(
            customer.birth_date ?? ''
          );
          customerObj.cust_birth_year = getBirthYear(customer.birth_date ?? '');
          setClarityCustomTags({ 'cust_age_segment': customerObj.cust_age_segment });
          break;
        case 'gender':
          customerObj.cust_gender = customer.gender ?? '';
          break;
        case 'birth_country':
          customerObj.cust_birth_country = customer.birth_country ?? '';
          break;
        case 'nationality':
          customerObj.cust_nationality = customer.nationality ?? '';
          break;
        case 'marital_status':
          customerObj.cust_marital_status = customer.marital_status ?? '';
          break;
        case 'contact_detail':
          customerObj.cust_has_email = customer.contact_detail.email
            ? 'true'
            : 'false';
          customerObj.cust_email_domain = getEmailDomain(
            customer.contact_detail.email ?? ''
          );
          customerObj.cust_has_mobile_phone = checkPhones(
            customer.contact_detail.phones
          )
            ? 'true'
            : 'false';
          break;
        case 'job':
          customerObj.cust_contract_type = customer.job.contract_type ?? '';
          customerObj.cust_monthly_income = customer.job.monthly_income
            ? customer.job.monthly_income.toString()
            : '';
          break;

        case 'unpaid_installments':
          debtObj.unpaid_installments_count = customer.unpaid_installments
            ? customer.unpaid_installments.toString()
            : '';
          break;
      }
    }
    customerObj.cust_card_registered_count = CardsList
      ? `${CardsList.length}`
      : '0';

    customerObj.cust_status =
      sessionStorage.getItem('flow') === 'co' ? 'CO' : 'ACT';
    setClarityCustomTags({'cust_status': customerObj.cust_status })
    dataLayerParamAdd('Customer', customerObj);
  },
  //customer/me -> person_id
  saveUserId: (value: number) => {
    //Lo guardamos como string, ya que por defecto se recibe como number
    dataLayerParamAdd('user_id', value.toString());
    
    // Y su evento correspondiente:
    const userData: GTMUserCargado = {
      user_id: value,
    }
    gevent(GAnalyticsEventType.user_cargado, userData);
  },

  //payment-methods/searches
  savePaymentMethodsDataLayer: (
    response: PaymentMethods,
    paymentMethodValue?: string
  ) => {
    if (response.payment_methods) {
      customerObj.cust_card_registered_count =
        response.payment_methods.length.toString();
    }
    const cardDataValue = getCardData(response.payment_methods);
    const paymentMethod = !paymentMethodValue
      ? sessionStorage.getItem('pepper-payment-method')
      : paymentMethodValue;
    if (
      paymentMethod &&
      (paymentMethod === PaymentMethod.CREDIT_CARD ||
        paymentMethod === PaymentMethod.EXPRESS)
    ) {
      if (cardDataValue === 'type') {
        paymentObj.payment_card_brand = response.payment_methods[0].type;
      } else if (cardDataValue === 'iin_number') {
        paymentObj.payment_iin_number = response.payment_methods[0].iin_number;
      }
    }
    if (paymentMethodValue) {
      paymentObj.payment_method = paymentMethodValue;
    } else {
      paymentObj.payment_method = recoverPaymentMethod();
    }
  },

  //debt/searches
  saveDebtDataLayer: (debt: DebtResponse, isCo?: boolean) => {
    let totalAmount;
    for (let value of Object.keys(debt)) {
      switch (value) {
        case 'loans':
          debtObj.unpaid_loans_count = debt.loans.length.toString();
          if (debt.loans.length > 0 && debt.loans[0].installments.length > 0) {
            debtObj.unpaid_installment_max_age =
              debt.loans[0].installments[0].days_past_due.toString();
            debtObj.unpaid_max_order_number =
              debt.loans[0].installments[0].order.toString();
            debtObj.unpaid_installments_count =
              debt.loans[0].installments.length.toString();
          }
          debtObj.unpaid_personal_loan_count = getLoanCount(
            debt.loans,
            'PERSONAL_LOAN'
          );
          debtObj.unpaid_consumer_loan_count = getLoanCount(
            debt.loans,
            'CONSUMER_LOAN'
          );

          // Payment Plan Data for CO
          if (isCo && debt.loans.length > 0) {
            debtObj.allow_payment_plan_count = getPaymentPlanCount(debt.loans);
            setClarityCustomTags({'allow_payment_plan_count': debtObj.allow_payment_plan_count});

            debtObj.payment_plan_installments_paid_count =
              getPaymentPlanStatesCount(debt.loans, PaymentPlanStatus.PAY);
            debtObj.payment_plan_installments_unpaid_count =
              getPaymentPlanStatesCount(debt.loans, PaymentPlanStatus.UNP);

            debtObj.payment_plan_min_start_day = getMinStartDay(debt.loans);
            debtObj.payment_plan_max_end_day = getMaxEndDay(debt.loans);
          }

          break;
        case 'amount':
          totalAmount = !isCo ? debt.amount : calculateTotalDebtAmount(debt);
          debtObj.unpaid_amount = totalAmount.toString();
          break;
        case 'promotions':
          if (debt.promotions.length > 0) {
            debtObj.unpaid_has_promotions = 'true';
            debtObj.unpaid_has_24h_promotion =
              debt.promotions[0].type === PromotionTypeEnum.H24
                ? 'true'
                : 'false';
            paymentObj.payment_savings_amount = !isCo
              ? `${debt.promotions[0].amount_excused}`
              : '0';
          } else {
            debtObj.unpaid_has_promotions = 'false';
            debtObj.unpaid_has_24h_promotion = 'false';
            paymentObj.payment_savings_amount = '0';
          }
          break;
      }
    }
    dataLayerParamAdd('Debt', debtObj);
  },

  //ZRU
  saveGoogleDeviceDataLayer: (googlePay: boolean) => {
    deviceObj.device_google_pay_capable = googlePay.toString();
  },

  saveAppleDeviceDataLayer: (ApplePay: boolean) => {
    deviceObj.device_apple_pay_capable = ApplePay.toString();

    dataLayerParamAdd('Device', deviceObj);
  },

  //POST: /payments
  savePaymentsDataLayer: (payments: PaymentDataLayer) => {
    for (let values of Object.keys(payments)) {
      switch (values) {
        case 'payment_id':
          paymentObj.payment_id = payments.payment_id;
          break;
        case 'transaction_reason':
          paymentObj.payment_transaction_reason = payments.transaction_reason;
          break;
        case 'transaction_type':
          paymentObj.payment_type = payments.transaction_type;
          break;
        case 'third_party':
          paymentObj.payment_third_used = payments.third_party ?? '';
          break;
        case 'status':
          paymentObj.payment_result = payments.status;
          break;
        case 'amount':
          paymentObj.payment_final_amount =
            payments.amount && parseFloat(payments.amount) > 0
              ? payments.amount
              : paymentObj.payment_final_amount;
          break;
        case 'amount_excused':
          paymentObj.payment_savings_amount =
            payments.amount_excused &&
            parseFloat(payments.amount_excused) > 0 &&
            paymentObj.payment_transaction_reason !==
              TransactionReasonEnum.DEC &&
            paymentObj.payment_transaction_reason !==
              TransactionReasonEnum.CPP &&
            paymentObj.payment_transaction_reason !==
              TransactionReasonEnum.DE1
              ? payments.amount_excused
              : paymentObj.payment_savings_amount;
      }
    }
    //Recupero el valor del storage con el método de pago que corresponda
    paymentObj.payment_method = recoverPaymentMethod();
  },

  savePaymentCardType: (value: string) => {
    paymentObj.payment_card_brand = value;
  },

  //POST: /reuse-payment
  saveReusePaymentDataLayer: (
    response: ReusePaymentResponse,
    optionalParams?: ReusePaymentOptionalParams,
  ) => {
    paymentObj.payment_final_amount = response.amount.toString();
    paymentObj.payment_result = response.status;
    if (paymentObj.payment_id === '') {
      paymentObj.payment_id = response.payment_method_id;
    }
    if (paymentObj.payment_type === '') {
      paymentObj.payment_type = response.transaction_type;
    }
    paymentObj.payment_method = recoverPaymentMethod();
    if (optionalParams) {
      paymentObj.payment_transaction_reason = optionalParams.transaction_reason ?? '';
      paymentObj.payment_savings_amount = optionalParams.excused_amount?.toString() ?? '0';
    }
  },

  //Promotion concept - cuando viene con category
  saveUsedPromotion: (value: boolean) => {
    paymentObj.payment_promotion_used = value ? 'true' : 'false';

    dataLayerParamAdd(`Payment`, paymentObj);
  },

  // **********************************************************************
  // EVENTS
  // **********************************************************************
  // **********************************************************************
  // GENERAL EVENTS
  // **********************************************************************

  sendNoDebtEvent: () => {
    gevent(GAnalyticsEventType.no_debt_entry);
  },

  sendAccessDeniedEvent: () => {
    gevent(GAnalyticsEventType.access_denied);
  },

  sendContactUsEvent: () => {
    gevent(GAnalyticsEventType.contact_us);
  },

  sendKoEvent: (step: GTMFlowSteps | undefined) => {
    if (step) {
      const stepData: GTMStepNumber = {
        step: step,
      };

      gevent(GAnalyticsEventType.event_ko, stepData);
      setClarityCustomTags({ [GAnalyticsEventType.event_ko]: step });
    }
  },

  sendCallBackEvent: (
    phoneValue: GTMCallBackPhone,
    when: GTMCallBackWhen,
    step: GTMFlowSteps
  ) => {
    const callBackData: GTMCallBackEvent = {
      step: step,
      phone: phoneValue,
      when: when,
    };
    gevent(GAnalyticsEventType.call_me_back, callBackData);
    setClarityCustomTags({ [GAnalyticsEventType.call_me_back]: 'done' });
  },

  // **********************************************************************
  // DEBT DETAIL EVENTS
  // **********************************************************************

  sendShowDebt: () => {
    gevent(GAnalyticsEventType.show_debt_details);
  },

  sendShowPaymentPlanDetails: () => {
    gevent(GAnalyticsEventType.show_payment_plan_debt_details);
  },

  sendShowPromotions: () => {
    gevent(GAnalyticsEventType.show_promotions);
  },

  sendShowPromotionsDetail: () => {
    gevent(GAnalyticsEventType.show_promotion_details);
  },

  sendChangeDebtType: (
    initTimeData: GTMTimeElapse['initTime'],
    paymentOption: StandardPaymentType | PromotionTypeEnum
  ) => {
    //Se recibe la hora inicial y se calcula la diferencia con la actual
    const timeElapsed: GTMTimeData = getTimeResult(initTimeData);

    const debtTypeChangedData: GTMChangePaymentOption = {
      time_elapsed: timeElapsed.time_elapsed,
      payment_option: paymentOption,
    };

    gevent(GAnalyticsEventType.change_debt_type, debtTypeChangedData);
  },

  sendFastPay: (
    initTimeData: GTMTimeElapse['initTime'],
    paymentOption: StandardPaymentType | PromotionTypeEnum,
    paymentPlanData?: GTMPaymentPlanData
  ) => {
    //Se recibe la hora inicial y se calcula la diferencia con la actual
    const timeElapsed: GTMTimeData = getTimeResult(initTimeData);

    let fastPayData: GTMFastPayData;

    if (!paymentPlanData) {
      fastPayData = {
        time_elapsed: timeElapsed.time_elapsed,
        payment_option: paymentOption,
      };
    } else {
      fastPayData = {
        time_elapsed: timeElapsed.time_elapsed,
        payment_option: paymentOption,
        payment_plan_data: paymentPlanData,
      };
    }
    gevent(GAnalyticsEventType.fastpay, fastPayData);
  },

  sendContinueDebtType: (
    initTimeData: GTMTimeElapse['initTime'],
    paymentOption: StandardPaymentType | PromotionTypeEnum,
    paymentPlanData?: GTMPaymentPlanData
  ) => {
    const timeElapsed: GTMTimeData = getTimeResult(initTimeData);
    let continueDebtTypeData: GTMFastPayData;
    if (!paymentPlanData) {
      continueDebtTypeData = {
        time_elapsed: timeElapsed.time_elapsed,
        payment_option: paymentOption,
      };
    } else {
      continueDebtTypeData = {
        time_elapsed: timeElapsed.time_elapsed,
        payment_option: paymentOption,
        payment_plan_data: paymentPlanData,
      };
    }
    gevent(GAnalyticsEventType.continue_debt_type, continueDebtTypeData);
  },

  // CO con varias operaciones
  sendSelectOperation: (loanId: string) => {
    gevent(GAnalyticsEventType.select_operation, { loan: loanId });
  },

  sendBackStart: () => {
    gevent(GAnalyticsEventType.back_start);
  },

  // **********************************************************************
  // PAYMENT METHODS EVENTS
  // **********************************************************************

  sendOtherOptions: () => {
    gevent(GAnalyticsEventType.show_other_options);
  },

  sendChangePaymentMethod: () => {
    gevent(GAnalyticsEventType.change_payment_method);
  },

  sendBackDebtType: (initTimeData: GTMTimeElapse['initTime']) => {
    const timeElapsed: GTMTimeData = getTimeResult(initTimeData);

    gevent(GAnalyticsEventType.back_debt_type, timeElapsed);
  },

  sendContinuePaymentMethod: (initTimeData: GTMTimeElapse['initTime']) => {
    const timeElapsed: GTMTimeData = getTimeResult(initTimeData);
    let savedPaymentMethod =
      sessionStorage.getItem('pepper-payment-method') ?? '';
    const continuePaymentData: GTMContinuePaymentMethod = {
      paymentMethod: savedPaymentMethod,
      ...timeElapsed,
    };

    gevent(GAnalyticsEventType.continue_payment_method, continuePaymentData);
  },

  // **********************************************************************
  // PAYMENT METHODS EVENTS
  // **********************************************************************

  sendPaymentError: (initTimeData: GTMTimeElapse['initTime'], paymentMethod?: PaymentMethod) => {
    const timeElapsed: GTMTimeData = getTimeResult(initTimeData);
    const paymentErrorData: GTMContinuePaymentMethod = {
      paymentMethod: paymentMethod || recoverPaymentMethod(),
      ...timeElapsed,
    };
    gevent(GAnalyticsEventType.payment_error, paymentErrorData);
    setClarityCustomTags({ [GAnalyticsEventType.payment_error]: 'done' });
  },

  sendPaymentInfo: (amount: number, paymentMethodId: string) => {
    const paymentInfoData: GTMPaymentInfo = {
      currency: 'EUR',
      value: amount,
      items: [
        { itemId: `payment-${paymentMethodId}`, itemName: 'Payment-Landing' },
      ],
    };

    gevent(GAnalyticsEventType.add_payment_info, paymentInfoData);
  },

  sendContinuePayment: (
    initTimeData: GTMTimeElapse['initTime'],
    step: GTMFlowSteps,
    paymentData: GTMPaymentData
  ) => {
    const timeElapsed: GTMTimeData = getTimeResult(initTimeData);

    const paymentInfoData: GTMContinuePayment = {
      time_elapsed: timeElapsed.time_elapsed,
      step: step,
      payment_method: paymentData.payment_method,
      payment_transaction_reason: paymentData.payment_transaction_reason,
      payment_iin_number: paymentData.payment_iin_number,
      payment_card_brand: paymentData.payment_card_brand,
      payment_third_used: paymentData.payment_third_used,
    };

    gevent(GAnalyticsEventType.continue_payment, paymentInfoData);
  },

  sendPurchase: (purchaseData: GTMPurchaseData) => {
    gevent(GAnalyticsEventType.purchase, purchaseData);
    setClarityCustomTags({ [GAnalyticsEventType.purchase]: 'done' });
  },

  // **********************************************************************
  // PAYMENT ADDITIONAL EVENTS
  // **********************************************************************

  sendShowInAdvancePayment: () => {
    gevent(GAnalyticsEventType.show_in_advance_payment);
  },

  sendInAdvancePayment: (initTimeData: GTMTimeElapse['initTime']) => {
    const timeElapsed: GTMTimeData = getTimeResult(initTimeData);

    gevent(GAnalyticsEventType.in_advance_payment, timeElapsed);
    setClarityCustomTags({ [GAnalyticsEventType.in_advance_payment]: 'done' });
  },
};

export default GAnalyticsService;
